import { initializeApp, getApps } from 'firebase/app';
import {
  getAuth,
  initializeAuth,
  indexedDBLocalPersistence,
  browserLocalPersistence,
  inMemoryPersistence,
  GoogleAuthProvider,
} from 'firebase/auth';
import { getFirestore, initializeFirestore } from 'firebase/firestore';
import { getStorage } from 'firebase/storage';
import { getFunctions } from 'firebase/functions';

const firebaseConfig = {
  apiKey: process.env.NEXT_PUBLIC_FIREBASE_API_KEY,
  authDomain: process.env.NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID,
  storageBucket: process.env.NEXT_PUBLIC_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.NEXT_PUBLIC_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.NEXT_PUBLIC_FIREBASE_APP_ID,
};

// Validate Firebase config
if (!firebaseConfig.apiKey || !firebaseConfig.projectId) {
  console.error(' Firebase configuration missing! Check your .env.local file.');
  console.error('Expected variables: NEXT_PUBLIC_FIREBASE_API_KEY, NEXT_PUBLIC_FIREBASE_PROJECT_ID, etc.');
}

console.log('� Initializing Firebase...', {
  projectId: firebaseConfig.projectId,
  authDomain: firebaseConfig.authDomain,
});

const app = getApps().length === 0 ? initializeApp(firebaseConfig) : getApps()[0];

/**
 * Detect the Capacitor native shell WITHOUT a hard @capacitor/core import
 * (keeps this early-loaded module SSR/build-safe). Capacitor injects
 * `window.Capacitor` before the app bundle runs.
 */
function isNativeShell(): boolean {
  try {
    if (typeof window === 'undefined') return false;
    const cap = (window as any).Capacitor;
    return !!(cap && typeof cap.isNativePlatform === 'function' && cap.isNativePlatform());
  } catch {
    return false;
  }
}

// iOS/Android WKWebView: the default getAuth() persistence probing (ITP /
// storage-availability checks) HANGS in the webview, which stalls
// signInWithCustomToken and produces "sign-in timed out / check your network."
// Per the Capawesome firebase-js-sdk guide + firebase-js-sdk#6504, initialize
// with an EXPLICIT persistence chain (first that works wins; in-memory can
// never hang) and NO popupRedirectResolver (we do native OAuth via the plugin,
// so we don't need the resolver's origin/DOM checks that also hang here).
const auth = isNativeShell()
  ? initializeAuth(app, {
      persistence: [indexedDBLocalPersistence, browserLocalPersistence, inMemoryPersistence],
    })
  : getAuth(app);
// ignoreUndefinedProperties: Firestore THROWS on any `undefined` field value,
// which rejects the ENTIRE write. That silently destroyed onboarding: tapping
// "No injuries" set `injuryNotes: undefined`, so the whole profile setDoc threw,
// got swallowed by a catch, and the user's bodyweight/age/experience were never
// saved — leaving them with no estimated 1RMs and a first workout prescribing
// 0 lb. Dropping undefined fields (instead of exploding) makes writes resilient
// to this entire class of bug app-wide.
// iOS/Android WKWebView: Firestore's default WebChannel STREAMING transport
// often never establishes inside the webview, so getDoc/onSnapshot/syncAll reads
// hang forever with no cached fallback. That was the App Store 2.1(a) "loads
// indefinitely after login" rejection — Auth was hardened for WKWebView above,
// but Firestore was NOT, so the hang moved to the first post-login dashboard read.
// Force long-polling on native (plain HTTP requests that work in WKWebView);
// keep the default streaming transport on web where it works and is faster.
const db = isNativeShell()
  ? initializeFirestore(app, {
      ignoreUndefinedProperties: true,
      experimentalForceLongPolling: true,
    })
  : initializeFirestore(app, { ignoreUndefinedProperties: true });
const storage = getStorage(app);
const functions = getFunctions(app, 'us-central1');
const googleProvider = new GoogleAuthProvider();

console.log(' Firebase initialized successfully');

export { app, auth, db, storage, functions, googleProvider };

